import { create } from 'zustand';
import { Notification, notificationService } from '../services/notification.service';

// Grouping logic - groups consecutive NEW_MESSAGE notifications from same conversation
export interface GroupedNotification extends Notification {
    count: number;
    isGroup: boolean;
    allIds: string[]; // All notification IDs in this group (for marking all as read)
}

export function groupNotifications(notifs: Notification[]): GroupedNotification[] {
    if (notifs.length === 0) return [];

    const grouped: GroupedNotification[] = [];
    let currentGroup: GroupedNotification | null = null;

    for (const notif of notifs) {
        // Only group NEW_MESSAGE notifications with same actionUrl AND same read status
        // This prevents merging new unread messages with old read messages
        if (notif.type === 'NEW_MESSAGE' && currentGroup?.type === 'NEW_MESSAGE'
            && notif.actionUrl === currentGroup.actionUrl
            && notif.read === currentGroup.read) {
            // Add to existing group
            currentGroup.count++;
            currentGroup.isGroup = true;
            currentGroup.allIds.push(notif.id);
            // Update read status (redundant now since we group by read status, but kept for safety)
            if (!notif.read) currentGroup.read = false;
        } else {
            // Start new group or add single notification
            if (currentGroup) grouped.push(currentGroup);
            currentGroup = {
                ...notif,
                count: 1,
                isGroup: false,
                allIds: [notif.id]
            };
        }
    }

    // Don't forget the last group
    if (currentGroup) grouped.push(currentGroup);

    return grouped;
}

interface NotificationState {
    notifications: Notification[];
    unreadCount: number;
    isConnected: boolean;
    isLoading: boolean;
    isFetchingUnreadCount: boolean;
    hasMore: boolean;
    page: number;

    // Computed - grouped notifications
    getGroupedNotifications: () => GroupedNotification[];

    // Actions
    setConnected: (connected: boolean) => void;
    setUnreadCount: (count: number) => void;
    addNotification: (notification: Notification) => void;
    fetchNotifications: (reset?: boolean) => Promise<void>;
    fetchUntilGroupedTarget: (targetCount: number) => Promise<void>;
    markAsRead: (id: string) => Promise<void>;
    markGroupAsRead: (ids: string[]) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    fetchUnreadCount: () => Promise<void>;
    reset: () => void;
}

const BATCH_SIZE = 20;
const TARGET_GROUPED_COUNT = 10;

export const useNotificationStore = create<NotificationState>((set, get) => ({
    notifications: [],
    unreadCount: 0,
    isConnected: false,
    isLoading: false,
    isFetchingUnreadCount: false,
    hasMore: true,
    page: 0,

    // Computed getter for grouped notifications
    getGroupedNotifications: () => groupNotifications(get().notifications),

    setConnected: (connected) => set({ isConnected: connected }),

    setUnreadCount: (count) => set({ unreadCount: count }),

    addNotification: (notification) => {
        set((state) => ({
            notifications: [notification, ...state.notifications],
            unreadCount: state.unreadCount + 1,
        }));
    },

    // Fetch a single batch
    fetchNotifications: async (reset = false) => {
        const { isLoading, page, notifications } = get();
        if (isLoading) return;

        const currentPage = reset ? 0 : page;
        set({ isLoading: true });

        try {
            const response = await notificationService.getNotifications(currentPage, BATCH_SIZE);
            set({
                notifications: reset ? (response?.items ?? []) : [...notifications, ...(response?.items ?? [])],
                hasMore: response?.hasNext ?? false,
                page: currentPage + 1,
                isLoading: false,
            });
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
            set({ isLoading: false });
        }
    },

    // Smart fetch: keeps loading until we have enough grouped items OR no more data
    fetchUntilGroupedTarget: async (targetCount = TARGET_GROUPED_COUNT) => {
        const { isLoading, hasMore } = get();
        if (isLoading) return;

        let currentHasMore = hasMore;
        let iterations = 0;
        const maxIterations = 10; // Safety limit to prevent infinite loops

        while (iterations < maxIterations) {
            iterations++;

            const grouped = get().getGroupedNotifications();

            // Stop if we have enough grouped items or no more to fetch
            if (grouped.length >= targetCount || !currentHasMore) {
                break;
            }

            // Fetch next batch
            const { page, notifications } = get();
            set({ isLoading: true });

            try {
                const response = await notificationService.getNotifications(page, BATCH_SIZE);
                const newNotifications = [...notifications, ...(response?.items ?? [])];

                set({
                    notifications: newNotifications,
                    hasMore: response?.hasNext ?? false,
                    page: page + 1,
                    isLoading: false,
                });

                currentHasMore = response.hasNext;
            } catch (error) {
                console.error('Failed to fetch notifications:', error);
                set({ isLoading: false });
                break;
            }
        }
    },

    markAsRead: async (id) => {
        try {
            await notificationService.markAsRead(id);
            set((state) => ({
                notifications: state.notifications.map((n) =>
                    n.id === id ? { ...n, read: true } : n
                ),
                unreadCount: Math.max(0, state.unreadCount - 1),
            }));
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    },

    // Mark all notifications in a group as read
    markGroupAsRead: async (ids) => {
        try {
            // Mark all unread IDs as read
            const { notifications } = get();
            const unreadIds = ids.filter(id => {
                const notif = notifications.find(n => n.id === id);
                return notif && !notif.read;
            });

            if (unreadIds.length === 0) return;

            // Use batch API endpoint
            await notificationService.markBatchAsRead(unreadIds);

            set((state) => ({
                notifications: state.notifications.map((n) =>
                    ids.includes(n.id) ? { ...n, read: true } : n
                ),
                unreadCount: Math.max(0, state.unreadCount - unreadIds.length),
            }));
        } catch (error) {
            console.error('Failed to mark group as read:', error);
        }
    },

    markAllAsRead: async () => {
        try {
            await notificationService.markAllAsRead();
            set((state) => ({
                notifications: state.notifications.map((n) => ({ ...n, read: true })),
                unreadCount: 0,
            }));
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    },

    fetchUnreadCount: async () => {
        if (get().isFetchingUnreadCount) return;
        set({ isFetchingUnreadCount: true });
        try {
            const count = await notificationService.getUnreadCount();
            set({ unreadCount: count, isFetchingUnreadCount: false });
        } catch (error) {
            console.error('Failed to fetch unread count:', error);
            set({ isFetchingUnreadCount: false });
        }
    },

    reset: () => {
        set({
            notifications: [],
            unreadCount: 0,
            isConnected: false,
            isLoading: false,
            isFetchingUnreadCount: false,
            hasMore: true,
            page: 0,
        });
    },
}));
